<template>
  <div class="opc-cfgcore" :class="{'opc-cfgcore--empty': getSelectedPlans.length == 0}" v-loading="!!isConfigCardLoading">
    <EmptyShoppingCart />
    <config-plans
      :filter-keys="draftPlanFilterKeys"
      @remove="removePlanByKey"
      @add="addon"
      :configPlanTileStatus="configPlanTileStatus"
    />
    <partner-plan-card />
    <clear-shopping-cart />
    <edu-tips v-if="showEduTips" />
    <template v-if="!isCartBuilderAddQty && getSelectedPlans.length">
    <addon-cards :group="ADDON_GROUP.BIZ_PLUS" v-if="addonable || isShowBizCard" :filter-keys="addonFilterKeys" @add="addon">
      <div class="opc-cfgcore__addon">
        <h2 class="opc-cfgcore__addon__title">{{ $t('billing.zobizplus.promo_biz_title') }}</h2>
        <span v-if="!unlockBizLicenseCount" class="opc-cfgcore__addon__desc">{{ $t('billing.zobizplus.promo_biz_desc') }}</span>
      </div>
    </addon-cards>
      <addon-cards :group="ADDON_GROUP.JOINT_SALES" v-if="addonable" :filter-keys="addonFilterKeys" @add="addon">
        <div class="opc-cfgcore__exclusive_addon">
          <div class="opc-cfgcore__exclusive_addon__title-row">
            <h2 class="opc-cfgcore__exclusive_addon__title text-capitalize">{{ $t('billing.partner.special_promo.title') }}</h2>
            <span class="opc-cfgcore__exclusive_addon__subtitle">{{ $t('billing.partner.special_promo.limit_time') }}</span>
          </div>
        </div>
      </addon-cards>
      <addon-cards :group="ADDON_GROUP.WIN_BACK" v-if="winBackAble" class="opc-cfgcore__addon-winback" :filter-keys="winBackFilterKeys" @add="addon">
        <div class="opc-cfgcore__addon">
          <h2 class="opc-cfgcore__addon__title text-capitalize">{{ $t('billing.previously_purchased') }}</h2>
        </div>
      </addon-cards>
    <addon-cards v-if="addonable" :filter-keys="addonFilterKeys" class="opc-cfgcore__addon-enhance" @add="addon" :default-show-num="addonDefaultShowNum">
      <div class="opc-cfgcore__addon" :class="winBackAble?'opc-cfgcore__no_boundary_addon':''">
        <h2 class="opc-cfgcore__addon__title text-capitalize">{{ subTitle }}</h2>
      </div>
    </addon-cards>
    </template>

    <learn-more-dialog :learn-more-config="learnMoreConfig" />
  </div>
</template>

<script>
import {
  planTypes,
  ADDON_GROUP,
  unlockBizLicenseCount,
  BASE_PLAN_SUB_TYPES,
  ADDON_ACCORDION_THRESHOLD,
  BILLING_CYCLE,
  baseMeetingTypeList,
  BUNDLE_WITH_ZP_BASE_PLAN_TYPES,
  ZOOM_MEETING_PRO_MIN_LICENSE, ZOOM_MEETING_EDU_SCH_CMP_MIN_LICENSE, ZOOM_MEETING_EDU_PLUS_MIN_LICENSE,
} from '@/common/constants';
import { AddOrRemoveBasePlanActionTypes } from '@/components/capacity/constants';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import ConfigPlans from './plans/index.js';
import AddonCards, { filterAddonForKeys } from './addon-cards';
import plans, { AllPlans, getPlans } from '@/plans';
import { mapProducts, getEntryPoint } from '@/aspects/trackings';
import { getRecommendPlansFrom } from '@/common/product.js';
import log from '@/common/log';
import { isBizPlusContext, isShowBizCard } from '@/plans/ZOOM_MEETING/common/utils';
import { isCartBuilderAddQty } from '@/utils/cart-builder';
import EduTips from './EduTips.vue'
import PartnerPlanCard from '@/views/config/partner-refer/plans/config.vue';
import EmptyShoppingCart from './shopping-cart/empty.vue'
import { isInUnlockBizPlusCountRetentionFlow } from '@/plans/ZOOM_ONE_BUNDLE/utils.js';
import mxSoftBundleTracking from '@/views/config/partner-refer/mixin/event-tracking'
import ClearShoppingCart from './shopping-cart/clear.vue'
import LearnMoreDialog from '@/components/learn-more-dialog.vue';
import { groupBy } from 'lodash';
import { plansof } from '@/utils/chain';
import { addDefaultPbxBasePlanToShoppingCartByQuery } from '@/flows/BUY_NOW';
import { de$type } from '@/common/service/flow/utils.js';

const CMR_PLAN_KEY = planTypes[planTypes.CMR]

export default {
  props: {
    dividerText: String,
    addonable: {
      type: Boolean,
      default: true,
    },
    addonFilter: {
      type: Function,
      default: () => true,
    }
  },
  components: {
    PartnerPlanCard,
    ConfigPlans,
    AddonCards,
    EduTips,
    EmptyShoppingCart,
    ClearShoppingCart,
    LearnMoreDialog,
  },
  mixins: [mxSoftBundleTracking],
  data() {
    return {
      isCartBuilderAddQty,
      ADDON_GROUP,
      // config plan tile expand or collapse status
      configPlanTileStatus: {},
      isBizPlusContext,
    };
  },
  computed: {
    ...mapGetters([
      'getSelectedPlans',
      'fromCampaignLink',
      'basicConfig',
      'isDisableAdd2Cart',
      'cmrPaywallTipVisible',
      'draftPlanFilterKeys',
      'boughtPlans',
      'ghostBasePlanKey',
      'freeTrialPlans',
      'pastPlans',
      'selectedPbxBasePlan',
      'allPlanMap',
      'learnMoreConfig',
      'isConfigCardLoading'
    ]),
    ...mapMutations(['addOrRemoveBasePlan']),
    ...mapState({
      planMapFlat: (state) => state.ShoppingCart.planMapFlat,
      addOrRemoveBasePlanState: state => state.Product.addOrRemoveBasePlanState,
    }),
    showEduTips() {
      return !!this.getSelectedPlans.find(sp => sp.type === planTypes.ZOOM_MEETING && sp.category === BASE_PLAN_SUB_TYPES.EDUCATION)
    },
    selectedBasePlanType() {
      return getRecommendPlansFrom(this.getSelectedPlans)?.[0]?.type;
    },
    selectedTypes() {
      return [...new Set(this.getSelectedPlans.map((x) => x.type))];
    },
    planKeys() {
      // each plan that has config & is allowed in plan list of the currency
      const keys = plans
        .filter((x) => !!x?.components?.config)
        .map((x) => x.key)
        .filter((key) => !!this.planMapFlat.find((x) => x.type === planTypes[key]));
      return keys;
    },
    addonFilterForKeys() {
      return planTypes.map([this.selectedBasePlanType]);
      /*
      const keys = planTypes.map([this.selectedBasePlanType]);
      if(this.ghostBasePlanKey){
        keys.push(this.ghostBasePlanKey)
      }
      return keys.filter(Boolean);
      */
    },
    allAddonPlans() {
      let sortedPlans = plans;
      if (this.getSelectedPlans.length === 1) {
        const { type, sub_type } = this.getSelectedPlans[0];
        sortedPlans = getPlans(type, sub_type);
      } else if (this.$context.type === planTypes.ZOOM_MEETING) {
        const subType = getRecommendPlansFrom(this.getSelectedPlans)?.[0]?.sub_type;
        sortedPlans = getPlans(this.selectedBasePlanType, subType);
      }
      return sortedPlans?.filter((p) => this.planKeys?.some(key => (
        key === p?.key ||
        (p?.key === 'ZOOM_DEVICE' && de$type(key) === 'ZOOM_DEVICE')
      )) && p.components?.addon) || [];
    },
    applicableAddonList() {
      if (this.isDisableAdd2Cart) {
        return [];
      }
      return filterAddonForKeys(
        this.allAddonPlans,
        this.addonFilterForKeys,
        this.getSelectedPlans,
        this.boughtPlans,
        this.freeTrialPlans,
        this.allPlanMap
      );
    },
    rawAddonFilterKeys() {
      return this.applicableAddonList.filter((p) => {
        return !this.draftPlanFilterKeys.some(key => (
          key === p.key ||
          // for zoom device, the key is zoom_device$desk_phone, but the plan type is zoom_device
          (p.key === 'ZOOM_DEVICE' && de$type(key) === 'ZOOM_DEVICE')
        ))
      }).map((x) => x.key).filter(this.addonFilter);
    },
    addonFilterKeys() {
      const keys = [...this.rawAddonFilterKeys]
      // Move CMR to the first
      let cmrPlanIndex = -1
      if (this.cmrPaywallTipVisible && (cmrPlanIndex = keys.indexOf(CMR_PLAN_KEY)) > 0) {
        keys.splice(cmrPlanIndex, 1)
        keys.unshift(CMR_PLAN_KEY)
      }
      const winBackHasBase = this.winBackKeys.some(key => baseMeetingTypeList.includes(planTypes[key]));
      return keys.filter(key => {
        if (this.winBackFilterKeys.includes(key)) return false;
        if (winBackHasBase && baseMeetingTypeList.includes(planTypes[key])) return false;
        return true;
      });
    },
    addonDefaultShowNum() {
      return this.winBackAble ? (ADDON_ACCORDION_THRESHOLD - this.winBackFilterKeys.length) + 1 : ADDON_ACCORDION_THRESHOLD;
    },
    winBackKeys() {
      if (!this.pastPlans?.length) return [];
      const list = AllPlans?.filter(x => x.components?.addon) || [];
      const validKeys = new Set(list.map(x => x.key));
      return (this.pastPlans || [])
        .map(item => item.semanticType)
        .filter(type => validKeys.has(type)) || [];
    },
    winBackFilterKeys() {
      if (!this.pastPlans?.length) return [];
      const draftSet = new Set(this.draftPlanFilterKeys);
      return this.winBackKeys.filter(type => !draftSet.has(type));
    },
    subTitle() {
      return this.dividerText || this.$t('billing.enhance_experience');
    },
    isShowBizCard() {
      if (this.$context.is.add2cart) return false;
      if (this.basicConfig.isBulkLicenseAccount) return false;
      if (this.$context.is.pro2bundle) return false;
      if (isInUnlockBizPlusCountRetentionFlow()) return false;
      if (this.$context.is.modalDelta) return false;
      return isBizPlusContext && isShowBizCard();
    },
    unlockBizLicenseCount() {
      return unlockBizLicenseCount;
    },
    // Has CMR addon or has added CMR
    hasCmrAddonOrAddedCmr() {
      return this.rawAddonFilterKeys.includes(CMR_PLAN_KEY) || this.draftPlanFilterKeys.includes(CMR_PLAN_KEY)
    },
    winBackAble() {
      return this.pastPlans.length > 0;
    },
  },
  watch: {
    draftPlanFilterKeys: {
      immediate: true,
      deep: true,
      handler(newFilterKeys = [], oldFiltersKeys = []) {
        log('config:plans', newFilterKeys);
        newFilterKeys = this.filterSpecialKeys(newFilterKeys);
        oldFiltersKeys = this.filterSpecialKeys(oldFiltersKeys);
        // add config plan panel
        if (newFilterKeys?.length > oldFiltersKeys?.length) {
          // reset config plan expand status
          this.configPlanTileStatus = {};
          newFilterKeys?.forEach((key) => {
            this.$set(this.configPlanTileStatus, key, !!this.selectedTypes.includes(planTypes[key]));
          });
        }
      },
    },
    addOrRemoveBasePlanState: {
      immediate: true,
      handler(val) {
        if(val?.planType && [AddOrRemoveBasePlanActionTypes.ADD, AddOrRemoveBasePlanActionTypes.ADD_PAID_BASE_PLAN_FOR_FREE_TRIAL_BASE_PLAN].includes(val?.actionType) && !this.draftPlanFilterKeys.includes(val?.planType)) {
          this.addon(val?.planType);
        }
        if(val?.planType && [AddOrRemoveBasePlanActionTypes.REMOVE, AddOrRemoveBasePlanActionTypes.REMOVE_PAID_BASE_PLAN_FOR_FREE_TRIAL_BASE_PLAN].includes(val?.actionType) && this.draftPlanFilterKeys.includes(val?.planType)) {
          this.removePlanByKey(val?.planType);
        }
      },
    },
    hasCmrAddonOrAddedCmr: {
      immediate: true,
      handler(data) {
        this.setHasCmrAddonOrAddedCmr(data)
      },
    },
  },
  provide() {
    return {
      $core: this,
    };
  },
  methods: {
    ...mapMutations(['removePlan', 'setHasCmrAddonOrAddedCmr', 'setDraftPlanFilterKeys','updateSelectPlan','setPlanMapFlat','setPbxBasePlanCode','savePersist']),
    ...mapActions(['removePlanByKey']),
    addon(defaultKey, customKey) {
      const key = customKey || defaultKey;
      // Add an empty draft plan
      const draftPlans = this.draftPlanFilterKeys.filter((k) => {
        return this.selectedTypes.includes(planTypes[k]);
      });
      if(draftPlans.includes(key)) return;
      this.setDraftPlanFilterKeys([...draftPlans, ...this.autoAddBasePlanHandle(key)]);
      this.scrollIntoView();
    },
    autoAddBasePlanHandle(key) {
      if(!this.winBackAble){
        return [key]
      }
      const { dependBasePlanTypeList } = this.planMapFlat.find(item => item.type === planTypes[key]);
      if (dependBasePlanTypeList && dependBasePlanTypeList.length > 0 && dependBasePlanTypeList.some(item => baseMeetingTypeList.includes(item)) && !this.selectedTypes.some(item => dependBasePlanTypeList.includes(item)) && !this.selectedTypes.some(item => baseMeetingTypeList.includes(item))) {
        const { planCode } = this.pastPlans.find(item => baseMeetingTypeList.includes(planTypes[item.semanticType])) || {};
        const item = planCode ? plansof(this.planMapFlat).code(planCode).get(0) : null;
        const category = item?.category || 'pro';
        const type = item?.type || planTypes.ZOOM_MEETING;
        this.autoAddBasePlanCartHandle(category,type);
        return [planTypes[type], key];
      } else {
        return [key];
      }
    },
    autoAddBasePlanCartHandle(category = 'pro', type = planTypes.ZOOM_MEETING) {
      const planInfo = this.planMapFlat.filter((item) => item.type === type);
      const planInfoGroupByCategory = groupBy(planInfo, 'category');
      const formPlan = planInfoGroupByCategory?.[category]?.find?.(
        (item) => item.billing_cycle === BILLING_CYCLE.ANNUAL,
      );
      if (BUNDLE_WITH_ZP_BASE_PLAN_TYPES.includes(type)) {
        this.$nextTick(() => {
          addDefaultPbxBasePlanToShoppingCartByQuery(this.planMapFlat);
          this.setPlanMapFlat([...this.planMapFlat]);
          if (this.selectedPbxBasePlan) {
            this.setPbxBasePlanCode(this.selectedPbxBasePlan.plan_code);
            this.savePersist()
          }
        });
      }
      let selectedNumber = 1;
      if (category === BASE_PLAN_SUB_TYPES.BIZ) {
        selectedNumber = ZOOM_MEETING_PRO_MIN_LICENSE;
      } else if (category === BASE_PLAN_SUB_TYPES.ZONE_EDU_SCH_CMP) {
        selectedNumber = ZOOM_MEETING_EDU_SCH_CMP_MIN_LICENSE;
      } else if (category === BASE_PLAN_SUB_TYPES.ZONE_EDU_PLUS) {
        selectedNumber = ZOOM_MEETING_EDU_PLUS_MIN_LICENSE;
      }
      this.updateSelectPlan({
        ...formPlan,
        cartInfo: {
          selected_number: selectedNumber,
        },
      });
    },
    scrollIntoView() {
      setTimeout(() => {
        const dom = document.querySelector('.opc-config .opc-tile:last-child');
        if (!dom) return;
        dom.style['scroll-margin-top'] = '140px';
        dom?.scrollIntoView({ behavior: 'smooth' });
      }, 1000);
    },
    removePlanByCode(key, plan) {
      this.removePlan(plan?.plan_code);
      // when the shopping cart remove all category plans, should remove this panel
      const isAddon = this.allAddonPlans?.some((p) => p.key === key);
      const currentSelectedPlans = this.getSelectedPlans.filter((plan) => plan.type === planTypes[key]);

      if (isAddon && currentSelectedPlans?.length === 0) {
        this.removeDraftPlanFilterKeysByKey(key);
      }
    },
    removeDraftPlanFilterKeysByKey(key) {
      const draftPlanFilterKeys = [...this.draftPlanFilterKeys];
      draftPlanFilterKeys.splice(
        this.draftPlanFilterKeys.findIndex((x) => x === key),
        1,
      );
      this.setDraftPlanFilterKeys(draftPlanFilterKeys);
    },
    filterSpecialKeys(keys) {
      return keys.filter(k => k !== planTypes.EVENT_SERVICES && k !== planTypes[planTypes.EVENT_SERVICES]);
    }
  },
  created() {
    let productKeys = this.$context.is.modal ?
      [...this.draftPlanFilterKeys] :
      [...this.draftPlanFilterKeys, ...this.addonFilterKeys]
    this.$tracking(() => ({
      event: 'Product View',
      products: mapProducts(
        productKeys.reduce((acc, key) => {
          const plans = this.planMapFlat.filter((plan) => plan.type === planTypes[key]);
          acc = [...acc, ...plans];
          return acc;
        }, []),
      ),
      ...this.getSoftBundleTrackingInfoForProductView(),
      ...getEntryPoint(),
    }));
  },
};
</script>

<style lang="scss" scoped>
@import '@zoom/billing-responsive/styles/responsive.scss';
@import '@/styles/scss-utils.scss';

.opc-cfgcore {
  &__addon, &__exclusive_addon {
    margin-top: 48px;
    padding-top: 48px;
    border-top: 2px solid #dfe3e8;
    @include darkTheme() {
      border-top: 2px solid #313235;
    }
  }
  &__no_boundary_addon {
    padding-top: 0px;
    border-top: none;
    @include darkTheme() {
      border-top: none;
    }
  }


  &__addon {
    margin-bottom: 24px;
    &__title {
      font-weight: 700;
      font-size: 22px;
      line-height: 150%;
    }
    &__desc{
      font-size: 14px;
      font-style: normal;
      font-weight: 450;
      line-height: 21px;
      color: #666487;
    }

  }

  &__exclusive_addon {
    &__title-row {
      display: flex;
      align-items: center;
      margin-bottom: 24px;
    }

    &__title {
      color: #000;
      font-family: Almaden Sans;
      font-size: 22px;
      font-style: normal;
      font-weight: 700;
      line-height: 150%; /* 33px */
      margin-right: 4px;
    }

    &__subtitle {
      color: #ffffff;
      text-align: center;
      font-family: 'SF Pro', 'Almaden Sans';
      font-size: 14px;
      font-style: normal;
      font-weight: 510;
      line-height: 20px;
      border-radius: 100px;
      background: linear-gradient(81deg, #493AB7 0%, #925DFF 100%);
      padding: 0px 6px;
    }
  }
}

@include media(mobile) {
  .opc-cfgcore {
    &__addon, &__exclusive_addon {
      margin-top: 48px;
      padding-top: 48px;
      border-top: 2px solid #dfe3e8;
    }

    &__no_boundary_addon {
      padding-top: 0px;
      border-top: none;
    }

    &__addon {
      margin-bottom: 24px;
      &__title {
        font-size: 22px;
      }
      &__desc{
        font-size: 14px;
        line-height: 21px;
      }

    }

    &__exclusive_addon {
      &__title-row {
        margin-bottom: 24px;
      }

      &__title {
        font-size: 22px;
        margin-right: 4px;
      }

      &__subtitle {
        font-size: 14px;
        line-height: 20px;
        border-radius: 100px;
        padding: 0px 6px;
      }
    }
  }
}
</style>

<style lang="scss">
.opc-config:has(.opc-cfgcore--empty) .banner-container, .opc-cfgcore--empty + .config-desc-slot{
  display: none;
}
</style>
